import { msg, plural } from '@lingui/core/macro';
import { useLingui } from '@lingui/react/macro';
import CopyIcon from '@mui/icons-material/ContentCopyOutlined';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import {
  Box,
  Button,
  CircularProgress,
  FormControlLabel,
  IconButton,
  Link,
  Radio,
  RadioGroup,
  Stack,
  Tooltip,
  Typography,
  useRadioGroup,
} from '@mui/material';
import { memo, type ReactNode, useCallback } from 'react';
import { BaseDrawer, drawerContentSx } from 'ui-kit';
import { useUiStore } from '@/common/uiStore.ts';
import { type DialogKey, useDialogStore } from '@/components/dialogs';
import { SlippageSelector } from '@/components/dialogs/slippage_selector.tsx';
import { useNotificationStore } from '@/feature/notifications';
import { OrderType } from '@/feature/order_entry/order_types';
import { environment } from '../../../../environment.ts';
import {
  LIQUIDATION_ALERT_COOLDOWN_CHOICES,
  LIQUIDATION_ALERT_MARGIN_USED_CHOICES,
  useLiquidationAlertsStore,
} from '../../../../features/account/liquidationAlertsStore.ts';
import { localeLabels } from '../../../../locales/i18n.ts';
import { useLocaleSelection } from '../../../../locales/useLocaleSelection.ts';
import useAppStore from '../../../../store/rootStore.ts';
import { LayoutAdvancedIcon } from '../LayoutAdvancedIcon.tsx';
import { LayoutLeftIcon } from '../LayoutLeftIcon.tsx';
import { LayoutRightIcon } from '../LayoutRightIcon.tsx';
import { LayoutSimpleIcon } from '../LayoutSimpleIcon.tsx';
import { useFontSelection } from '../useFontSelection.ts';
import { useOneClickTrading } from '../useOneClickTrading.ts';
import { ChartOverlayPreferencesSection } from './chart_overlay_preference_section.tsx';
import { NotificationPreferencesSection } from './notification_preference_section.tsx';
import { PreferenceSection } from './preference_section.tsx';
import { PreferenceSelect } from './preference_select.tsx';
import { PreferenceSwitch } from './preference_switch.tsx';
import { TerminalPanelsSection } from './terminal_panels_section.tsx';

const DIALOG_KEY: DialogKey = 'userPreferencesDrawer';

const layoutModeSettings = {
  trading: [
    { value: 'Advanced', icon: <LayoutAdvancedIcon /> },
    { value: 'Simple', icon: <LayoutSimpleIcon /> },
  ],
  layout: [
    { value: 'Left', icon: <LayoutLeftIcon /> },
    { value: 'Right', icon: <LayoutRightIcon /> },
  ],
};

function LayoutModeFormControl({ value, icon, label }: { value: string; icon: ReactNode; label: string }) {
  const radioGroup = useRadioGroup();
  const checked = radioGroup?.value === value;

  return (
    <FormControlLabel
      value={value}
      label={
        <Typography
          variant="body2"
          sx={{
            color: checked ? 'text.primary' : 'text.secondary',
            mt: 0.5,
          }}
        >
          {label}
        </Typography>
      }
      labelPlacement="bottom"
      sx={{ m: 0 }}
      control={
        <Radio
          disableRipple
          icon={icon}
          checkedIcon={icon}
          sx={(theme) => ({
            p: 0,
            borderRadius: '4px',
            border: '1px solid transparent',
            '&:hover': {
              borderColor: theme.vars.palette.primary.main,
              backgroundColor: 'transparent',
            },
            '&.Mui-checked': { borderColor: theme.vars.palette.primary.main },
          })}
        />
      }
    />
  );
}

export function UserPreferencesDrawer() {
  const { t, i18n } = useLingui();
  const { currentLocale, locales, selectLocale } = useLocaleSelection();
  const languageLabel = t({ comment: 'Label of the language picker in the preferences drawer', message: 'Language' });
  const isOpen = useDialogStore((state) => !!state.openDialogs[DIALOG_KEY]);
  const closeDialog = useDialogStore((state) => state.closeDialog);
  const openDialog = useDialogStore((state) => state.openDialog);

  const { isOneClickEnabled, handleToggleChange, isPending, isAuthorized } = useOneClickTrading();
  const { selectedFont, fontOptions, handleFontChange, resetFont } = useFontSelection();

  const resetNotificationPreferences = useNotificationStore((state) => state.resetPreferences);

  const reverseChartPosition = useUiStore((state) => state.reverseChartPosition);
  const setReverseChartPosition = useUiStore((state) => state.setReverseChartPosition);
  const resetLayoutPreferences = useUiStore((state) => state.resetLayoutPreferences);
  const confirmPositionClose = useUiStore((state) => state.confirmPositionClose);
  const setConfirmPositionClose = useUiStore((state) => state.setConfirmPositionClose);
  const confirmPositionFlip = useUiStore((state) => state.confirmPositionFlip);
  const setConfirmPositionFlip = useUiStore((state) => state.setConfirmPositionFlip);
  const defaultOrderType = useUiStore((state) => state.defaultOrderType);
  const setDefaultOrderType = useUiStore((state) => state.setDefaultOrderType);
  const customSlippagePercent = useUiStore((state) => state.customSlippagePercent);
  const setCustomSlippagePercent = useUiStore((state) => state.setCustomSlippagePercent);

  const defaultSlippage = useAppStore((state) => state.getSelectedMarket()?.orderMaxImpactP);
  const customSlippageEnabled = useAppStore((state) => state.config?.features.customSlippageEnabled);
  const flipPositionEnabled = useAppStore((state) => !!state.config?.features?.flipPositionEnabled);
  const languageSelectionEnabled = useAppStore((state) => state.config?.features?.languageSelection ?? false);

  const liquidationAlertsEnabled = useLiquidationAlertsStore((state) => state.enabled);
  const liquidationMarginUsedPercent = useLiquidationAlertsStore((state) => state.marginUsedPercent);
  const liquidationCooldownMinutes = useLiquidationAlertsStore((state) => state.cooldownMinutes);
  const setLiquidationAlertsEnabled = useLiquidationAlertsStore((state) => state.setEnabled);
  const setLiquidationMarginUsedPercent = useLiquidationAlertsStore((state) => state.setMarginUsedPercent);
  const setLiquidationCooldownMinutes = useLiquidationAlertsStore((state) => state.setCooldownMinutes);
  const resetLiquidationAlertPreferences = useLiquidationAlertsStore((state) => state.resetPreferences);

  const handleClose = () => closeDialog(DIALOG_KEY);

  const handleResetPreferences = useCallback(() => {
    resetLayoutPreferences();
    resetNotificationPreferences();
    resetLiquidationAlertPreferences();
    resetFont();
  }, [resetLayoutPreferences, resetNotificationPreferences, resetLiquidationAlertPreferences, resetFont]);

  const onToggleChange = (checked: boolean) => {
    if (!checked && isOneClickEnabled) {
      openDialog('confirmationDialog', {
        title: t({ comment: 'Disable one-click trading confirmation title', message: 'Disable One-Click Trading?' }),
        content: t({
          comment: 'Disable one-click trading confirmation body',
          message:
            'Turning off one-click trading will cancel all open orders with stop parameters (Stop Loss, Take Profit, etc.). Are you sure you want to proceed?',
        }),
        confirmLabel: t({ comment: 'Disable one-click trading confirm button', message: 'OK' }),
        cancelLabel: t({ comment: 'Disable one-click trading cancel button', message: 'Cancel' }),
        onConfirm: () => handleToggleChange(false),
      });
    } else {
      handleToggleChange(checked);
    }
  };

  const settingsTitle = t({ comment: 'Preferences drawer title', message: 'Settings' });
  const transactionsTitle = t({ comment: 'Preferences: transactions section title', message: 'Transactions' });
  const oneClickLabel = t({ comment: 'Preferences: one-click trading toggle', message: 'Enable One Click Trading' });
  const confirmCloseLabel = t({ comment: 'Preferences: confirm position close toggle', message: 'Confirm Position Close' });
  const confirmFlipLabel = t({
    comment: 'Preferences: confirm position flip (reverse) toggle',
    message: 'Confirm Position Flip',
  });
  const defaultOrderTypeLabel = t({
    comment: 'Preferences: select for the order type the order form opens on (Market or Limit)',
    message: 'Default Order Type',
  });
  const defaultOrderTypeOptions = [
    { value: OrderType.market, label: t({ comment: 'Market order type', message: 'Market' }) },
    { value: OrderType.limit, label: t({ comment: 'Limit order type', message: 'Limit' }) },
  ] as const;
  const slippageLabel = t({
    comment: 'Preferences: custom slippage heading',
    message: 'Maximum slippage for market orders',
  });
  const slippageHelp = t({
    comment: 'Preferences: custom slippage help text',
    message: 'Fills up to this limit, then cancels any remainder.',
  });
  const liquidationAlertsTitle = t({ comment: 'Preferences: liquidation alerts section title', message: 'Liquidation Alerts' });
  const liquidationWarnLabel = t({
    comment: 'Preferences: liquidation alerts toggle',
    message: 'Warn me when a position is approaching liquidation',
  });
  const alertAtLabel = t({ comment: 'Preferences: liquidation alert threshold select', message: 'Alert at' });
  const cooldownLabel = t({ comment: 'Preferences: liquidation alert cooldown select', message: 'Alert cooldown' });
  const interfaceTitle = t({ comment: 'Preferences: interface section title', message: 'Interface' });
  const fontLabel = t({ comment: 'Preferences: interface font select', message: 'Interface Font' });
  const layoutLeftLabel = t({ context: 'chart layout side', comment: 'Preferences: chart layout left', message: 'Left' });
  const layoutRightLabel = t({ context: 'chart layout side', comment: 'Preferences: chart layout right', message: 'Right' });

  return (
    <BaseDrawer
      open={isOpen}
      onClose={handleClose}
      title={settingsTitle}
      closeAriaLabel={t({ comment: 'Preferences drawer close button', message: 'close settings' })}
      headerAction={
        <Button variant="text" size="small" onClick={handleResetPreferences}>
          {t({ comment: 'Preferences drawer reset button', message: 'Reset Layout' })}
        </Button>
      }
      footer={<SettingsFooter />}
    >
      <Box sx={{ ...drawerContentSx, p: 2 }}>
        <Stack>
          <PreferenceSection title={transactionsTitle}>
            <Stack gap={1}>
              <PreferenceSwitch
                label={
                  <Box display="flex" alignItems="center" gap={1}>
                    <Typography variant="body2">{oneClickLabel}</Typography>
                    {isPending && <CircularProgress size={16} />}
                  </Box>
                }
                checked={!!isOneClickEnabled}
                onChange={onToggleChange}
                disabled={isPending || !isAuthorized}
              />
              <PreferenceSwitch label={confirmCloseLabel} checked={confirmPositionClose} onChange={setConfirmPositionClose} />
              {flipPositionEnabled ? (
                <PreferenceSwitch label={confirmFlipLabel} checked={confirmPositionFlip} onChange={setConfirmPositionFlip} />
              ) : null}
              <PreferenceSelect
                id="default-order-type"
                label={defaultOrderTypeLabel}
                value={defaultOrderType}
                onChange={setDefaultOrderType}
                options={defaultOrderTypeOptions}
              />
              {customSlippageEnabled && (
                <Box sx={{ mt: 1 }}>
                  <Typography variant="body2">{slippageLabel}</Typography>
                  <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 1 }}>
                    {slippageHelp}
                  </Typography>
                  <SlippageSelector
                    defaultSlippage={defaultSlippage}
                    value={customSlippagePercent ?? defaultSlippage ?? null}
                    onChange={(value, valid) => {
                      if (!valid) return;
                      const isBackendDefault = value == null || (defaultSlippage != null && value === defaultSlippage);
                      setCustomSlippagePercent(isBackendDefault ? null : value);
                    }}
                  />
                </Box>
              )}
            </Stack>
          </PreferenceSection>

          <NotificationPreferencesSection />

          <PreferenceSection title={liquidationAlertsTitle} titleAdornment={<LiquidationAlertsInfo />}>
            <Stack gap={1}>
              <PreferenceSwitch
                label={liquidationWarnLabel}
                checked={liquidationAlertsEnabled}
                onChange={setLiquidationAlertsEnabled}
              />
              <PreferenceSelect
                id="liquidation-alert-level"
                label={alertAtLabel}
                value={liquidationMarginUsedPercent}
                disabled={!liquidationAlertsEnabled}
                onChange={setLiquidationMarginUsedPercent}
                options={LIQUIDATION_ALERT_MARGIN_USED_CHOICES.map((percent) => ({
                  value: percent,
                  label: t({
                    comment: 'Preferences: liquidation alert threshold option',
                    message: `${{ percent }}% of the way to liquidation`,
                  }),
                }))}
              />
              <PreferenceSelect
                id="liquidation-cooldown"
                label={cooldownLabel}
                value={liquidationCooldownMinutes}
                disabled={!liquidationAlertsEnabled}
                onChange={setLiquidationCooldownMinutes}
                options={LIQUIDATION_ALERT_COOLDOWN_CHOICES.map((minutes) => ({
                  value: minutes,
                  label:
                    minutes < 60
                      ? i18n._(
                          msg({
                            comment: 'Preferences: liquidation alert cooldown duration',
                            message: plural({ minutes }, { one: '# minute', other: '# minutes' }),
                          })
                        )
                      : i18n._(
                          msg({
                            comment: 'Preferences: liquidation alert cooldown duration',
                            message: plural({ hours: minutes / 60 }, { one: '# hour', other: '# hours' }),
                          })
                        ),
                }))}
              />
            </Stack>
          </PreferenceSection>

          <TerminalPanelsSection />

          <ChartOverlayPreferencesSection />

          <PreferenceSection title={interfaceTitle}>
            <Stack gap={1}>
              <RadioGroup
                row
                sx={{ justifyContent: 'space-around' }}
                aria-labelledby="layout-mode-group-label"
                value={reverseChartPosition ? 'Left' : 'Right'}
                onChange={(e) => setReverseChartPosition(e.target.value === 'Left')}
                name="radio-buttons-group"
              >
                {layoutModeSettings.layout.map((option) => (
                  <LayoutModeFormControl
                    key={option.value}
                    value={option.value}
                    icon={option.icon}
                    label={option.value === 'Left' ? layoutLeftLabel : layoutRightLabel}
                  />
                ))}
              </RadioGroup>
              {/* Duplicated on purpose: the header globe is the fast path, this is where users go
                  looking for "settings". Both drive `useLocaleSelection`, so they can't diverge. */}
              {languageSelectionEnabled && (
                <PreferenceSelect
                  id="language-select"
                  label={languageLabel}
                  value={currentLocale}
                  onChange={selectLocale}
                  options={locales.map((locale) => ({
                    value: locale,
                    label: localeLabels[locale],
                  }))}
                />
              )}
              <PreferenceSelect
                id="font-select"
                label={fontLabel}
                value={selectedFont}
                onChange={handleFontChange}
                options={fontOptions.map((font) => ({
                  value: font.id,
                  label: (
                    <Typography variant="body2" style={{ fontFamily: font.value }}>
                      {font.id === 'system'
                        ? t({ comment: 'Preferences: system default font option', message: 'System Default' })
                        : font.name}
                    </Typography>
                  ),
                }))}
              />
            </Stack>
          </PreferenceSection>
        </Stack>
      </Box>
    </BaseDrawer>
  );
}

function LiquidationAlertsInfo() {
  const { t } = useLingui();

  return (
    <Tooltip
      arrow
      placement="top"
      slotProps={{ tooltip: { sx: { maxWidth: 300 } } }}
      title={
        <Typography variant="caption" color="inherit">
          {t({
            comment: 'Preferences: liquidation alerts info tooltip',
            message:
              'Triggers on how far the mark price has moved from your entry toward your liquidation price. Adding margin lowers it.',
          })}
        </Typography>
      }
    >
      <InfoOutlinedIcon
        fontSize="small"
        tabIndex={0}
        aria-label={t({ comment: 'Preferences: liquidation alerts info icon', message: 'How liquidation alerts work' })}
        sx={{ color: 'action.active', cursor: 'help' }}
      />
    </Tooltip>
  );
}

const SettingsFooter = memo(() => {
  const { t } = useLingui();
  const discordUrl = useAppStore((state) => state.config?.features.discordUrl);
  const accountId = useAppStore((state) => state.account?.id);
  const hasSupportInfo = Boolean(window?.xBrowserSessionId) || accountId != null;
  const copySupportLabel = t({ comment: 'Preferences drawer copy support info', message: 'Copy support info' });

  const handleCopy = useCallback(async () => {
    const parts: string[] = [];
    if (window?.xBrowserSessionId) parts.push(`Session ID: ${window.xBrowserSessionId}`);
    if (accountId != null) parts.push(`Account ID: ${accountId}`);

    const textToCopy = parts.join('; ');
    if (!textToCopy) return;

    try {
      await navigator.clipboard.writeText(textToCopy);
    } catch (err) {
      console.error('Failed to copy to clipboard:', err);
    }
  }, [accountId]);

  return (
    <Box width="100%" display="flex" justifyContent="space-between" alignItems="center">
      <Typography variant="caption" color="text.secondary">
        {t({
          comment: 'Preferences drawer UI version',
          message: `UI version: ${{ version: environment.appVersion }}`,
        })}
      </Typography>
      <Box display="flex" alignItems="center" gap={0.5}>
        <Link underline="hover" variant="body2" component="a" href={discordUrl} target="_blank" aria-label="Discord">
          {t({ comment: 'Preferences drawer help link', message: 'Help' })}
        </Link>
        <Tooltip
          title={
            hasSupportInfo ? copySupportLabel : t({ comment: 'Preferences drawer no support info', message: 'No support info' })
          }
          arrow
        >
          <span>
            <IconButton size="small" onClick={handleCopy} disabled={!hasSupportInfo} aria-label={copySupportLabel}>
              <CopyIcon fontSize="inherit" />
            </IconButton>
          </span>
        </Tooltip>
      </Box>
    </Box>
  );
});

SettingsFooter.displayName = 'SettingsFooter';
